import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchForm, FormField, FormFieldType } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useMe } from '../session/SessionProvider';
import { FIELD_TYPE_LABELS } from './labels';

type Draft = FormField & { optionsText: string };

const needsOptions = (t: FormFieldType) => t === 'choice' || t === 'multi';
const toDraft = (f: FormField): Draft => ({ ...f, optionsText: (f.options ?? []).join('\n') });

/** Crear o editar un borrador. Las claves de las preguntas no cambian para no romper respuestas. */
export function FormBuilderPage() {
  const { formId } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const counter = useRef(1);
  const [loaded, setLoaded] = useState<'loading' | 'ready' | unknown>(formId ? 'loading' : 'ready');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [onePerPerson, setOnePerPerson] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [fields, setFields] = useState<Draft[]>([]);
  const [busy, setBusy] = useState<'draft' | 'publish' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const newKey = (existing: Draft[]) => {
    let key = `p${counter.current++}`;
    while (existing.some((f) => f.key === key)) key = `p${counter.current++}`;
    return key;
  };

  useEffect(() => {
    if (!formId || !churchId) return;
    const controller = new AbortController();
    api
      .get<{ form: ChurchForm }>(`/forms/churches/${churchId}/forms/${formId}`, controller.signal)
      .then(({ form }) => {
        setTitle(form.title);
        setDescription(form.description ?? '');
        setOnePerPerson(form.onePerPerson);
        setRequiresApproval(form.requiresApproval);
        setFields(form.fields.map(toDraft));
        setLoaded('ready');
      })
      .catch((e: unknown) => !controller.signal.aborted && setLoaded(e));
    return () => controller.abort();
  }, [churchId, formId]);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (loaded === 'loading') return <Loading />;
  if (loaded !== 'ready') return <ErrorState error={loaded} />;

  const update = (i: number, patch: Partial<Draft>) => setFields((all) => all.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, by: number) =>
    setFields((all) => {
      const next = [...all];
      const [item] = next.splice(i, 1);
      next.splice(i + by, 0, item!);
      return next;
    });

  /** «Guardar borrador» o «Guardar y publicar» (#70): publicar lo abre para responder en el mismo paso. */
  async function onSubmit(event: FormEvent, publish = false) {
    event.preventDefault();
    setBusy(publish ? 'publish' : 'draft');
    setError(null);
    const body = {
      title,
      description: description || null,
      onePerPerson,
      requiresApproval,
      fields: fields.map(({ optionsText, ...f }) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: f.required,
        ...(needsOptions(f.type) ? { options: optionsText.split('\n').map((o) => o.trim()).filter(Boolean) } : {}),
      })),
    };
    try {
      const saved = formId ? await api.put<ChurchForm>(`/forms/churches/${churchId}/forms/${formId}`, body) : await api.post<ChurchForm>(`/forms/churches/${churchId}/forms`, body);
      if (publish) await api.post(`/forms/churches/${churchId}/forms/${formId ?? saved.id}/open`);
      navigate('/app/formularios?vista=administrar');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/formularios?vista=administrar">← Formularios</Link>
      </p>
      <header className="page__header">
        <h1>{formId ? 'Editar formulario' : 'Nuevo formulario'}</h1>
        <p className="muted">«Guardar borrador» lo guarda sin que nadie lo vea; «Guardar y publicar» lo abre para que lo respondan.</p>
      </header>
      <form className="form" onSubmit={onSubmit} noValidate>
        <Card>
          <Field label="Título" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={150} />
          <div className="field">
            <label htmlFor="form-description">Descripción (opcional)</label>
            <textarea id="form-description" rows={2} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <label className="choice">
            <input type="checkbox" checked={onePerPerson} onChange={(e) => setOnePerPerson(e.target.checked)} />
            <span>Una respuesta por persona</span>
          </label>
          <label className="choice">
            <input type="checkbox" checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />
            <span>Cada respuesta necesita aprobación</span>
          </label>
        </Card>

        {fields.map((f, i) => (
          <Card key={f.key} title={`Pregunta ${i + 1}`}>
            <Field label="Texto de la pregunta" value={f.label} onChange={(e) => update(i, { label: e.target.value })} maxLength={200} />
            <div className="field">
              <label htmlFor={`type-${f.key}`}>Tipo de respuesta</label>
              <select id={`type-${f.key}`} value={f.type} onChange={(e) => update(i, { type: e.target.value as FormFieldType })}>
                {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {needsOptions(f.type) ? (
              <div className="field">
                <label htmlFor={`options-${f.key}`}>Opciones (una por línea)</label>
                <textarea id={`options-${f.key}`} rows={3} value={f.optionsText} onChange={(e) => update(i, { optionsText: e.target.value })} />
              </div>
            ) : null}
            <label className="choice">
              <input type="checkbox" checked={f.required} onChange={(e) => update(i, { required: e.target.checked })} />
              <span>Obligatoria</span>
            </label>
            <div className="actions">
              <Button type="button" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Subir pregunta ${i + 1}`}>
                Subir
              </Button>
              <Button type="button" variant="ghost" disabled={i === fields.length - 1} onClick={() => move(i, 1)} aria-label={`Bajar pregunta ${i + 1}`}>
                Bajar
              </Button>
              <Button type="button" variant="ghost" onClick={() => setFields((all) => all.filter((_, j) => j !== i))} aria-label={`Quitar pregunta ${i + 1}`}>
                Quitar
              </Button>
            </div>
          </Card>
        ))}

        {error ? <Alert>{error}</Alert> : null}
        <div className="actions">
          <Button type="button" variant="secondary" onClick={() => setFields((all) => [...all, { key: newKey(all), label: '', type: 'text', required: false, optionsText: '' }])}>
            Agregar pregunta
          </Button>
          <Button type="submit" variant="secondary" busy={busy === 'draft'} disabled={busy !== null}>
            Guardar borrador
          </Button>
          <Button type="button" busy={busy === 'publish'} disabled={busy !== null} onClick={(e) => void onSubmit(e, true)}>
            Guardar y publicar
          </Button>
        </div>
      </form>
    </div>
  );
}
